import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import KPICard from '../components/KPICard';
import { useAuthFetch } from '../hooks/useAuthFetch';
import { useFilters } from '../hooks/useFilters';
import { printFacture } from '../utils/printDocument';

const fmt = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(n || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const STATUT_COLORS = { Brouillon: '#94a3b8', Envoyée: '#3b82f6', Payée: '#22c55e', 'En retard': '#ef4444' };

const Badge = ({ text, color }) => (
  <span style={{ background: color + '22', color, border: `1px solid ${color}44`, borderRadius: 6, padding: '2px 9px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>{text}</span>
);
const inputStyle = { padding: '8px 12px', borderRadius: 8, background: '#1a1d2e', border: '1px solid #2a2d3e', color: '#e2e8f0', fontSize: 13, fontFamily: 'inherit', outline: 'none' };

function FacturePanel({ factureId, onClose, onSave }) {
  const fetchWithAuth = useAuthFetch();
  const [facture, setFacture] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPaiement, setShowPaiement] = useState(false);
  const [modePaiement, setModePaiement] = useState('Virement');
  const [showRelance, setShowRelance] = useState(false);
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const load = useCallback(() => {
    fetchWithAuth(`/api/factures/${factureId}`).then(r => r.json()).then(setFacture);
  }, [factureId]);

  useEffect(() => { load(); }, [load]);

  const setStatut = async (statut) => {
    setSaving(true);
    await fetchWithAuth(`/api/factures/${factureId}/statut`, { method: 'PATCH', body: JSON.stringify({ statut }) });
    load(); setSaving(false); onSave?.();
  };

  const payer = async () => {
    setSaving(true);
    await fetchWithAuth(`/api/factures/${factureId}/payer`, { method: 'PATCH', body: JSON.stringify({ mode_paiement: modePaiement }) });
    setShowPaiement(false); load(); setSaving(false); onSave?.();
  };

  const del = async () => {
    if (!window.confirm('Supprimer cette facture ?')) return;
    await fetchWithAuth(`/api/factures/${factureId}`, { method: 'DELETE' });
    onClose(); onSave?.();
  };

  const envoyer = async () => {
    setSending(true); setSendResult(null);
    try {
      const res = await fetchWithAuth(`/api/factures/${factureId}/envoyer`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) {
        setSendResult({ ok: true, msg: `Facture envoyée à ${data.email}` });
        load(); onSave?.();
      } else {
        setSendResult({ ok: false, msg: data.error });
      }
    } catch {
      setSendResult({ ok: false, msg: 'Erreur de connexion' });
    } finally { setSending(false); }
  };

  if (!facture) return null;
  const tva = facture.montant_ht * (facture.taux_tva / 100);
  const ttc = facture.montant_ht + tva;

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 99 }} />
      <div className="side-panel" style={{ width: 480 }}>
        <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid #2a2d3e' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <div>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 2, fontFamily: 'DM Mono, monospace' }}>{facture.numero}</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#e2e8f0' }}>{facture.titre}</div>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 20 }}>✕</button>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Badge text={facture.statut} color={STATUT_COLORS[facture.statut] || '#64748b'} />
            <span style={{ fontSize: 12, color: '#64748b' }}>Émise le {fmtDate(facture.date_emission)}</span>
          </div>
        </div>

        <div className="modal-scroll" style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          <div style={{ background: '#41B07315', border: '1px solid #41B07330', borderRadius: 8, padding: '8px 12px', marginBottom: 16, fontSize: 13, color: '#94a3b8' }}>
            🏢 <span style={{ color: '#e2e8f0' }}>{facture.client_nom}</span>
          </div>

          {/* Dates */}
          <div className="form-grid" style={{ marginBottom: 16 }}>
            {[['📅 Échéance', fmtDate(facture.date_echeance)], ['💳 Paiement', fmtDate(facture.date_paiement)]].map(([label, val]) => (
              <div key={label} style={{ background: '#12141f', borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>{label}</div>
                <div style={{ fontSize: 13, color: '#e2e8f0' }}>{val}</div>
              </div>
            ))}
          </div>
          {facture.mode_paiement && (
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>Mode : <span style={{ color: '#94a3b8' }}>{facture.mode_paiement}</span></div>
          )}

          {/* Lignes */}
          {(facture.lignes || []).length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16, fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #2a2d3e' }}>
                  {['Description', 'Qté', 'PU HT', 'Total HT'].map(h => (
                    <th key={h} style={{ padding: '6px 8px', textAlign: 'left', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', fontSize: 10 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {facture.lignes.map((l, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #2a2d3e22' }}>
                    <td style={{ padding: '8px', color: '#e2e8f0' }}>{l.description}</td>
                    <td style={{ padding: '8px', color: '#94a3b8', textAlign: 'right' }}>{l.quantite}</td>
                    <td style={{ padding: '8px', color: '#94a3b8', textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{fmt(l.prix_unitaire)}</td>
                    <td style={{ padding: '8px', color: '#41B073', textAlign: 'right', fontFamily: 'DM Mono, monospace', fontWeight: 600 }}>{fmt(l.quantite * l.prix_unitaire)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Totaux */}
          <div style={{ background: '#0f111780', borderRadius: 8, padding: 14, marginBottom: 16 }}>
            {[['Total HT', fmt(facture.montant_ht), '#e2e8f0'], [`TVA (${facture.taux_tva}%)`, fmt(tva), '#94a3b8'], ['Total TTC', fmt(ttc), '#22c55e']].map(([l, v, c]) => (
              <div key={l} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 13 }}>
                <span style={{ color: '#64748b' }}>{l}</span>
                <span style={{ color: c, fontFamily: 'DM Mono, monospace', fontWeight: l === 'Total TTC' ? 700 : 400 }}>{v}</span>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
            {facture.statut === 'Brouillon' && (
              <button onClick={() => setStatut('Envoyée')} disabled={saving} style={{ padding: '8px 14px', borderRadius: 8, background: '#3b82f620', border: '1px solid #3b82f640', color: '#3b82f6', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>
                📨 Marquer Envoyée
              </button>
            )}
            {['Envoyée', 'En retard'].includes(facture.statut) && (
              <button onClick={() => setShowPaiement(p => !p)} disabled={saving} style={{ padding: '8px 14px', borderRadius: 8, background: '#22c55e20', border: '1px solid #22c55e40', color: '#22c55e', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>
                💳 Enregistrer paiement
              </button>
            )}
            {facture.statut === 'En retard' && (
              <button onClick={() => setShowRelance(true)} style={{ padding: '8px 14px', borderRadius: 8, background: '#ef444420', border: '1px solid #ef444440', color: '#ef4444', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>
                📧 Relance
              </button>
            )}
          </div>

          {showPaiement && (
            <div style={{ background: '#12141f', border: '1px solid #2a2d3e', borderRadius: 8, padding: 14, marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>Mode de paiement</div>
              <select value={modePaiement} onChange={e => setModePaiement(e.target.value)} style={{ ...inputStyle, width: '100%', marginBottom: 10, cursor: 'pointer' }}>
                {['Virement', 'Chèque', 'CB', 'Prélèvement', 'Espèces'].map(m => <option key={m}>{m}</option>)}
              </select>
              <button onClick={payer} disabled={saving} style={{ width: '100%', padding: '9px', borderRadius: 8, background: '#22c55e', border: 'none', color: '#fff', cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit' }}>
                Confirmer le paiement
              </button>
            </div>
          )}

          {sendResult && (
            <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 8, fontSize: 12, background: sendResult.ok ? '#10b98120' : '#ef444420', border: `1px solid ${sendResult.ok ? '#10b98140' : '#ef444440'}`, color: sendResult.ok ? '#10b981' : '#ef4444' }}>
              {sendResult.ok ? '✓' : '✕'} {sendResult.msg}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, paddingTop: 12, borderTop: '1px solid #2a2d3e', flexWrap: 'wrap' }}>
            <button onClick={envoyer} disabled={sending} style={{ padding: '8px 14px', borderRadius: 8, background: '#3b82f620', border: '1px solid #3b82f640', color: '#3b82f6', cursor: sending ? 'not-allowed' : 'pointer', fontSize: 13, fontFamily: 'inherit' }}>
              {sending ? '⏳ Envoi...' : '📧 Envoyer au client'}
            </button>
            <button onClick={() => printFacture(facture)} style={{ padding: '8px 14px', borderRadius: 8, background: 'transparent', border: '1px solid #41B07340', color: '#41B073', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>
              📄 PDF
            </button>
            <button onClick={del} style={{ padding: '8px 14px', borderRadius: 8, background: 'transparent', border: '1px solid #ef444440', color: '#ef4444', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>
              🗑 Supprimer
            </button>
          </div>
        </div>
      </div>

      {showRelance && (() => {
        const tva = facture.montant_ht * (facture.taux_tva / 100);
        const ttc = facture.montant_ht + tva;
        const objet = `Relance : Facture ${facture.numero} en attente de règlement`;
        const corps = `Madame, Monsieur,

Sauf erreur de notre part, nous n'avons pas encore reçu le règlement de la facture suivante :

  Facture n° : ${facture.numero}
  Objet      : ${facture.titre}
  Montant TTC: ${new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(ttc)}
  Échéance   : ${facture.date_echeance ? new Date(facture.date_echeance).toLocaleDateString('fr-FR') : '—'}

Nous vous remercions de bien vouloir procéder au règlement dans les meilleurs délais.

En cas de règlement déjà effectué, veuillez ignorer ce message et nous transmettre votre justificatif.

Restant disponibles pour toute question, nous vous adressons nos cordiales salutations.

LA DONNÉE`;
        const full = `Objet : ${objet}\n\n${corps}`;
        const copy = () => { navigator.clipboard.writeText(full); setCopied(true); setTimeout(() => setCopied(false), 2500); };
        return (
          <>
            <div onClick={() => setShowRelance(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 200 }} />
            <div className="modal-scroll center-modal" style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 520, maxHeight: '80vh', overflowY: 'auto', background: '#1a1d2e', border: '1px solid #2a2d3e', borderRadius: 14, padding: 24, zIndex: 201 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ margin: 0, fontSize: 15, color: '#e2e8f0' }}>📧 Email de relance</h3>
                <button onClick={() => setShowRelance(false)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 18 }}>✕</button>
              </div>
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4, textTransform: 'uppercase' }}>Objet</div>
                <div style={{ fontSize: 13, color: '#e2e8f0', background: '#12141f', borderRadius: 6, padding: '8px 12px', border: '1px solid #2a2d3e' }}>{objet}</div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4, textTransform: 'uppercase' }}>Corps du message</div>
                <pre style={{ fontSize: 12, color: '#94a3b8', background: '#12141f', borderRadius: 6, padding: '12px', border: '1px solid #2a2d3e', whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0, lineHeight: 1.7 }}>{corps}</pre>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={copy} style={{ flex: 1, padding: '9px', borderRadius: 8, background: copied ? '#22c55e' : '#41B073', border: 'none', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit', transition: 'background 0.2s' }}>
                  {copied ? '✓ Copié !' : '📋 Copier le message complet'}
                </button>
                <button onClick={() => setShowRelance(false)} style={{ padding: '9px 16px', borderRadius: 8, background: 'none', border: '1px solid #2a2d3e', color: '#94a3b8', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>Fermer</button>
              </div>
            </div>
          </>
        );
      })()}
    </>
  );
}

export default function FacturesPage() {
  const fetchWithAuth = useAuthFetch();
  const location = useLocation();
  const { search, setSearch, page, setPage, limit, setLimit, sortField, sortDir, toggleSort, filters, setFilter, buildQuery } = useFilters({ sortField: 'created_at', sortDir: 'desc' });
  const [facturesList, setFacturesList] = useState([]);
  const [total, setTotal] = useState(0);
  const [kpis, setKpis] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(location.state?.factureId || null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res, all] = await Promise.all([
        fetchWithAuth(`/api/factures?${buildQuery()}`).then(r => r.json()),
        fetchWithAuth('/api/factures?limit=999').then(r => r.json()),
      ]);
      setFacturesList(res.data || []); setTotal(res.total || 0);
      const counts = { total: 0, Brouillon: 0, Envoyée: 0, Payée: 0, 'En retard': 0, caEncaisse: 0 };
      (all.data || []).forEach(f => {
        counts.total++;
        if (counts[f.statut] !== undefined) counts[f.statut]++;
        if (f.statut === 'Payée') counts.caEncaisse += f.montant_ht || 0;
      });
      setKpis(counts);
    } finally { setLoading(false); }
  }, [search, page, limit, sortField, sortDir, JSON.stringify(filters)]);

  useEffect(() => { load(); }, [load]);

  const SortTh = ({ field, children }) => (
    <th onClick={() => toggleSort(field)} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none' }}>
      {children} {sortField === field ? (sortDir === 'asc' ? '↑' : '↓') : ''}
    </th>
  );
  const totalPages = Math.ceil(total / limit);

  return (
    <div style={{ padding: 28 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#e2e8f0' }}>Factures</h1>
      </div>

      <div className="kpi-grid" style={{ marginBottom: 24 }}>
        <KPICard title="Total" value={kpis.total || 0} color="#41B073" icon="🧾" onClick={() => setFilter('statut', '')} />
        <KPICard title="Brouillons" value={kpis.Brouillon || 0} color="#94a3b8" onClick={() => setFilter('statut', 'Brouillon')} />
        <KPICard title="Envoyées" value={kpis.Envoyée || 0} color="#3b82f6" onClick={() => setFilter('statut', 'Envoyée')} />
        <KPICard title="Payées" value={kpis.Payée || 0} color="#22c55e" onClick={() => setFilter('statut', 'Payée')} />
        <KPICard title="En retard" value={kpis['En retard'] || 0} color="#ef4444" alert={kpis['En retard'] > 0} onClick={() => setFilter('statut', 'En retard')} />
        <KPICard title="CA encaissé" value={new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(kpis.caEncaisse || 0)} color="#22c55e" />
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Rechercher..." style={{ ...inputStyle, minWidth: 200 }} />
        <select value={filters.statut || ''} onChange={e => setFilter('statut', e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
          <option value="">Tous statuts</option>
          {['Brouillon', 'Envoyée', 'Payée', 'En retard'].map(s => <option key={s}>{s}</option>)}
        </select>
        <select value={limit} onChange={e => { setLimit(Number(e.target.value)); setPage(1); }} style={{ ...inputStyle, cursor: 'pointer' }}>
          {[25, 50, 100].map(n => <option key={n} value={n}>{n} / page</option>)}
        </select>
      </div>

      <div style={{ background: '#1a1d2e', border: '1px solid #2a2d3e', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #2a2d3e' }}>
                <SortTh field="numero">Numéro</SortTh>
                <SortTh field="titre">Titre</SortTh>
                <th style={{ padding: '10px 14px', fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', textAlign: 'left' }}>Client</th>
                <SortTh field="montant_ht">Montant HT</SortTh>
                <th style={{ padding: '10px 14px', fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', textAlign: 'left' }}>TTC</th>
                <SortTh field="statut">Statut</SortTh>
                <SortTh field="date_emission">Émise</SortTh>
                <SortTh field="date_echeance">Échéance</SortTh>
                <SortTh field="date_paiement">Payée le</SortTh>
              </tr>
            </thead>
            <tbody>
              {loading
                ? <tr><td colSpan={9} style={{ padding: 30, textAlign: 'center', color: '#64748b' }}>Chargement...</td></tr>
                : facturesList.length === 0
                  ? <tr><td colSpan={9} style={{ padding: 30, textAlign: 'center', color: '#64748b' }}>Aucune facture</td></tr>
                  : facturesList.map((f, i) => (
                    <tr key={f.id} onClick={() => setSelectedId(f.id)}
                      style={{ borderBottom: '1px solid #2a2d3e', cursor: 'pointer', background: i % 2 === 0 ? 'transparent' : '#ffffff05' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#41B07315'}
                      onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? 'transparent' : '#ffffff05'}>
                      <td style={{ padding: '11px 14px', fontSize: 12, color: '#94a3b8', fontFamily: 'DM Mono, monospace' }}>{f.numero}</td>
                      <td style={{ padding: '11px 14px', fontSize: 13, color: '#e2e8f0', fontWeight: 500 }}>{f.titre}</td>
                      <td style={{ padding: '11px 14px', fontSize: 13, color: '#94a3b8' }}>{f.client_nom}</td>
                      <td style={{ padding: '11px 14px', fontSize: 13, color: '#41B073', fontFamily: 'DM Mono, monospace' }}>{fmt(f.montant_ht)}</td>
                      <td style={{ padding: '11px 14px', fontSize: 13, color: '#94a3b8', fontFamily: 'DM Mono, monospace' }}>{fmt(f.montant_ht * (1 + f.taux_tva / 100))}</td>
                      <td style={{ padding: '11px 14px' }}><Badge text={f.statut} color={STATUT_COLORS[f.statut] || '#64748b'} /></td>
                      <td style={{ padding: '11px 14px', fontSize: 13, color: '#94a3b8' }}>{fmtDate(f.date_emission)}</td>
                      <td style={{ padding: '11px 14px', fontSize: 13, color: f.statut === 'En retard' ? '#ef4444' : '#94a3b8' }}>{fmtDate(f.date_echeance)}</td>
                      <td style={{ padding: '11px 14px', fontSize: 13, color: '#22c55e' }}>{fmtDate(f.date_paiement)}</td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: '1px solid #2a2d3e' }}>
          <span style={{ fontSize: 13, color: '#64748b' }}>{total} facture{total > 1 ? 's' : ''}</span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ padding: '5px 12px', borderRadius: 6, background: 'transparent', border: '1px solid #2a2d3e', color: page === 1 ? '#2a2d3e' : '#94a3b8', cursor: page === 1 ? 'default' : 'pointer', fontSize: 13 }}>←</button>
            <span style={{ padding: '5px 12px', fontSize: 13, color: '#94a3b8' }}>Page {page} / {totalPages || 1}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} style={{ padding: '5px 12px', borderRadius: 6, background: 'transparent', border: '1px solid #2a2d3e', color: page >= totalPages ? '#2a2d3e' : '#94a3b8', cursor: page >= totalPages ? 'default' : 'pointer', fontSize: 13 }}>→</button>
          </div>
        </div>
      </div>

      {selectedId && <FacturePanel factureId={selectedId} onClose={() => setSelectedId(null)} onSave={load} />}
    </div>
  );
}
